import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSearch, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListRecipes,
  useGetRecipe,
  useUpdateRecipe,
  useDeleteRecipe,
  useValidateRecipe,
  useFlagRecipe,
  useReextractRecipe,
  useMergeRecipes,
  useSplitRecipe,
  getListRecipesQueryKey,
  getGetRecipeQueryKey,
  getGetRecipeQueryOptions,
  type RecipeDetail,
  type UpdateRecipeRequest,
} from "@workspace/api-client-react";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Flag,
  RefreshCw,
  Trash2,
  GitMerge,
  Scissors,
  Save,
  Loader2,
  AlertCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { ImageViewer } from "@/components/validate/ImageViewer";
import {
  IngredientsEditor,
  type LocalIngredient,
} from "@/components/validate/IngredientsEditor";
import { NutritionPanel, type NutritionData } from "@/components/validate/NutritionPanel";
import { ScaleControl } from "@/components/validate/ScaleControl";
import { cn } from "@/lib/utils";

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface FormState {
  title: string;
  authorSource: string;
  category: string;
  cuisine: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  yieldServings: string;
  difficulty: string;
  directions: string;
  servingSuggestions: string;
  handwrittenNotes: string;
  transcriberNotes: string;
  sourceType: string;
  ingredients: LocalIngredient[];
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  "Main Dish", "Side", "Bread", "Beverage", "Dessert", "Appetizer",
  "Soup/Stew", "Salad", "Sauce/Condiment", "Snack", "Breakfast", "Other",
];

const SOURCE_TYPES = ["handwritten", "printed", "mixed"];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];

function recipeToForm(recipe: RecipeDetail): FormState {
  return {
    title: recipe.title ?? "",
    authorSource: recipe.authorSource ?? "",
    category: recipe.category ?? "",
    cuisine: recipe.cuisine ?? "",
    prepTime: recipe.prepTime ?? "",
    cookTime: recipe.cookTime ?? "",
    totalTime: recipe.totalTime ?? "",
    yieldServings: recipe.yieldServings ?? "",
    difficulty: recipe.difficulty ?? "",
    directions: recipe.directions ?? "",
    servingSuggestions: recipe.servingSuggestions ?? "",
    handwrittenNotes: recipe.handwrittenNotes ?? "",
    transcriberNotes: recipe.transcriberNotes ?? "",
    sourceType: recipe.sourceType ?? "",
    ingredients: (recipe.ingredients ?? []).map((ing, idx) => ({
      _key: ing.id ? `db-${ing.id}` : `idx-${idx}-${Date.now()}`,
      id: ing.id,
      sortOrder: ing.sortOrder,
      amount: ing.amount ?? null,
      unit: ing.unit ?? null,
      item: ing.item,
      prepNotes: ing.prepNotes ?? null,
      sectionHeader: ing.sectionHeader ?? null,
    })),
  };
}

function formToUpdate(form: FormState): UpdateRecipeRequest {
  return {
    title: form.title || undefined,
    authorSource: form.authorSource || null,
    category: form.category || null,
    cuisine: form.cuisine || null,
    prepTime: form.prepTime || null,
    cookTime: form.cookTime || null,
    totalTime: form.totalTime || null,
    yieldServings: form.yieldServings || null,
    difficulty: form.difficulty || null,
    directions: form.directions || undefined,
    servingSuggestions: form.servingSuggestions || null,
    handwrittenNotes: form.handwrittenNotes || null,
    transcriberNotes: form.transcriberNotes || null,
    sourceType: form.sourceType || null,
    ingredients: form.ingredients.map((ing) => ({
      sortOrder: ing.sortOrder,
      amount: ing.amount,
      unit: ing.unit,
      item: ing.item || "Untitled",
      prepNotes: ing.prepNotes,
      sectionHeader: ing.sectionHeader,
    })),
  };
}

function statusColor(status: string) {
  switch (status) {
    case "validated": return "bg-green-100 text-green-800 border-green-200";
    case "needs_correction": return "bg-amber-100 text-amber-800 border-amber-200";
    case "unreviewed": return "bg-blue-100 text-blue-700 border-blue-200";
    default: return "bg-muted text-muted-foreground";
  }
}

function statusLabel(status: string) {
  switch (status) {
    case "needs_correction": return "Needs Correction";
    case "validated": return "Validated";
    case "unreviewed": return "Unreviewed";
    default: return status;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────────────

export default function ValidatePage() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Mobile tab toggle — defaults to "recipe" so users land on something they can edit.
  const [mobileTab, setMobileTab] = useState<"recipe" | "image">("recipe");

  // URL state
  const params = new URLSearchParams(search);
  const idParam = params.get("id");
  const currentId = idParam ? parseInt(idParam, 10) : null;

  // Filter
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Load full list for navigation
  const listParams = statusFilter !== "all" ? { validationStatus: statusFilter } : undefined;
  const { data: listData, isLoading: listLoading } = useListRecipes(listParams);
  const recipes = listData?.recipes ?? [];

  const currentIndex = currentId ? recipes.findIndex((r) => r.id === currentId) : -1;

  // Auto-select first recipe when none is selected, or when the current recipe
  // falls outside the filtered list after a filter change.
  useEffect(() => {
    if (recipes.length > 0 && !listLoading && (!currentId || currentIndex === -1)) {
      navigate(`/admin/validate?id=${recipes[0].id}`, { replace: true });
    }
  }, [currentId, currentIndex, recipes.length, listLoading]);

  const navTo = useCallback((id: number) => {
    navigate(`/admin/validate?id=${id}`);
  }, [navigate]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < recipes.length - 1;

  // Load current recipe
  const { data: recipe, isLoading: recipeLoading } = useGetRecipe(
    currentId ?? 0,
    { query: { ...getGetRecipeQueryOptions(currentId ?? 0), enabled: !!currentId } }
  );

  // Nutrition state (used by NutritionPanel and quantity scaler)
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);

  // Scale state — resets to 1 when navigating to a different recipe
  const [scaleFactor, setScaleFactor] = useState(1);
  useEffect(() => { setScaleFactor(1); }, [currentId]);

  // Form state
  const [form, setForm] = useState<FormState | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Revision counter: increments on every user edit; prevents in-flight saves from clearing dirty
  const editRevisionRef = useRef(0);
  const savedRevisionRef = useRef(-1);

  // Sync form when recipe loads or ID changes
  useEffect(() => {
    if (recipe) {
      setForm(recipeToForm(recipe));
      setIsDirty(false);
      setSaveState("idle");
    }
  }, [recipe?.id]);

  // Mutations
  const updateMutation = useUpdateRecipe({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetRecipeQueryKey(updated.id), updated);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        setSaveState("saved");
        // Only clear dirty if no new edits occurred while the save was in-flight
        if (editRevisionRef.current === savedRevisionRef.current) {
          setIsDirty(false);
        }
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setSaveState("idle"), 3000);
      },
      onError: () => {
        setSaveState("error");
      },
    },
  });

  // After validating: advance to next unreviewed recipe
  const advanceToNextUnreviewed = useCallback(() => {
    const nextUnreviewed = recipes.find(
      (r, idx) => idx > currentIndex && r.validationStatus === "unreviewed"
    );
    if (nextUnreviewed) {
      navTo(nextUnreviewed.id);
    } else if (hasNext) {
      navTo(recipes[currentIndex + 1].id);
    }
  }, [recipes, currentIndex, hasNext, navTo]);

  const validateMutation = useValidateRecipe({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetRecipeQueryKey(updated.id), updated);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: "Recipe validated ✓", variant: "default", duration: 1800 });
        advanceToNextUnreviewed();
      },
    },
  });

  const flagMutation = useFlagRecipe({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetRecipeQueryKey(updated.id), updated);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: "Marked Needs Correction", variant: "default", duration: 1800 });
      },
    },
  });

  const deleteMutation = useDeleteRecipe({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: "Recipe deleted", variant: "default" });
        if (hasNext) navTo(recipes[currentIndex + 1].id);
        else if (hasPrev) navTo(recipes[currentIndex - 1].id);
        else navigate("/admin/validate");
      },
    },
  });

  const reextractMutation = useReextractRecipe({
    mutation: {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetRecipeQueryKey(updated.id), updated);
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        toast({ title: "Re-extraction complete — recipe data updated", variant: "default" });
      },
      onError: () => {
        toast({ title: "Re-extraction failed", variant: "destructive" });
      },
    },
  });

  const mergeMutation = useMergeRecipes({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        queryClient.setQueryData(getGetRecipeQueryKey(data.recipe.id), data.recipe);
        toast({ title: "Recipes merged successfully", variant: "default" });
        setMergeDialog(false);
        navTo(data.recipe.id);
      },
    },
  });

  const splitMutation = useSplitRecipe({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });
        queryClient.setQueryData(getGetRecipeQueryKey(data.original.id), data.original);
        toast({ title: "Recipe duplicated — new recipe created", variant: "default" });
        setSplitDialog(false);
        setSplitNewTitle("");
      },
    },
  });

  // Auto-save (2s debounce)
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isDirty || !currentId || !form) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveState("idle");
    autoSaveTimerRef.current = setTimeout(() => {
      setSaveState("saving");
      // Snapshot the revision at fire-time; onSuccess will only clear dirty if no newer edits
      savedRevisionRef.current = editRevisionRef.current;
      updateMutation.mutate({ id: currentId, data: formToUpdate(form) });
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [form, isDirty, currentId]);

  // Immediate save function
  const saveNow = useCallback(() => {
    if (!currentId || !form || !isDirty) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveState("saving");
    savedRevisionRef.current = editRevisionRef.current;
    updateMutation.mutate({ id: currentId, data: formToUpdate(form) });
  }, [currentId, form, isDirty]);

  // Update field helper — increments editRevision so in-flight saves don't clobber unsaved edits
  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => prev ? { ...prev, [key]: value } : prev);
    editRevisionRef.current++;
    setIsDirty(true);
  }, []);

  // Draggable divider
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [leftPct, setLeftPct] = useState(45);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(25, Math.min(72, pct)));
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [reextractDialog, setReextractDialog] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [splitDialog, setSplitDialog] = useState(false);
  const [splitNewTitle, setSplitNewTitle] = useState("");

  const mergeOptions = useMemo(
    () => recipes.filter((r) => r.id !== currentId),
    [recipes, currentId]
  );

  // Validate with save-first then advance
  const handleValidate = useCallback(() => {
    if (!currentId) return;
    const doValidate = () => validateMutation.mutate({ id: currentId });
    if (isDirty && form) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      setSaveState("saving");
      savedRevisionRef.current = editRevisionRef.current;
      updateMutation.mutate(
        { id: currentId, data: formToUpdate(form) },
        { onSuccess: doValidate }
      );
    } else {
      doValidate();
    }
  }, [currentId, isDirty, form, updateMutation, validateMutation]);

  // Apply extraction from ImageViewer highlight tool
  const handleApplyExtraction = useCallback(
    (
      dataType: string,
      parsed: Record<string, unknown>,
      _highlightId: number
    ) => {
      if (!form) return;

      let updates: Partial<FormState> = {};
      let toastMsg = "";

      switch (dataType) {
        case "notes":
        case "other": {
          const text = String(parsed.text ?? "");
          updates.handwrittenNotes = form.handwrittenNotes
            ? `${form.handwrittenNotes}\n${text}`
            : text;
          toastMsg = "Notes added to recipe";
          break;
        }
        case "directions": {
          const dir = String(parsed.directions ?? "");
          updates.directions = form.directions
            ? `${form.directions}\n${dir}`
            : dir;
          toastMsg = "Directions added to recipe";
          break;
        }
        case "title":
          updates.title = String(parsed.title ?? "");
          toastMsg = "Title updated";
          break;
        case "author":
          updates.authorSource = String(parsed.author_source ?? "");
          toastMsg = "Author/source updated";
          break;
        case "yield":
          updates.yieldServings = String(parsed.yield_servings ?? "");
          toastMsg = "Yield/servings updated";
          break;
        case "times": {
          if (parsed.prep_time != null)
            updates.prepTime = String(parsed.prep_time);
          if (parsed.cook_time != null)
            updates.cookTime = String(parsed.cook_time);
          if (parsed.total_time != null)
            updates.totalTime = String(parsed.total_time);
          toastMsg = "Times updated";
          break;
        }
        case "ingredients": {
          const newIngs = (
            parsed.ingredients as Array<{
              amount?: string | null;
              unit?: string | null;
              item?: string;
              prep_notes?: string | null;
              section_header?: string | null;
            }>
          ) ?? [];
          const lastSort = Math.max(
            0,
            ...form.ingredients.map((i) => i.sortOrder ?? 0)
          );
          const added = newIngs.map((ing, idx) => ({
            _key: `highlight-${Date.now()}-${idx}`,
            id: undefined as number | undefined,
            sortOrder: lastSort + idx + 1,
            amount: ing.amount ?? null,
            unit: ing.unit ?? null,
            item: ing.item ?? "Ingredient",
            prepNotes: ing.prep_notes ?? null,
            sectionHeader: ing.section_header ?? null,
          }));
          updates.ingredients = [...form.ingredients, ...added];
          toastMsg = `${added.length} ingredient${added.length !== 1 ? "s" : ""} added`;
          break;
        }
        default:
          return;
      }

      if (Object.keys(updates).length > 0) {
        setForm((prev) => (prev ? { ...prev, ...updates } : prev));
        editRevisionRef.current++;
        setIsDirty(true);
        toast({ title: toastMsg });
      }
    },
    [form, setForm, editRevisionRef, setIsDirty, toast]
  );

  // Keyboard shortcuts — must come after handleValidate
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();

      // Ctrl+Left / Ctrl+Right for navigation (work even in inputs)
      if ((e.ctrlKey || e.metaKey) && e.key === "ArrowLeft") {
        e.preventDefault();
        if (hasPrev) navTo(recipes[currentIndex - 1].id);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "ArrowRight") {
        e.preventDefault();
        if (hasNext) navTo(recipes[currentIndex + 1].id);
        return;
      }

      // Single-key shortcuts — skip when focus is in a text field
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === "v" && currentId) handleValidate();
      if (e.key === "f" && currentId) flagMutation.mutate({ id: currentId });
      if (e.key === "s" && currentId) saveNow();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasPrev, hasNext, currentIndex, recipes, currentId, saveNow, handleValidate]);

  // ── Render: no recipes ────────────────────────────────────────────────────
  if (!listLoading && recipes.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
          <AlertCircle className="w-12 h-12 text-muted-foreground" />
          <h2 className="text-2xl font-serif font-bold">No Recipes Yet</h2>
          <p className="text-muted-foreground max-w-sm">
            Extract some recipes from your images on the Dashboard first.
          </p>
          <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
            Go to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  const sourceImages = recipe?.sourceImages ?? [];

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Toolbar + (on mobile) tab toggle.
          On phone-sized viewports the page content can exceed the viewport height,
          causing the document body to scroll. Wrapping the toolbar and the mobile
          tab toggle in a single sticky container pins them just below the 64px
          page header so prev/next, save state, filter, etc. stay reachable while
          the user scrolls through the recipe form. Desktop keeps the original
          in-flow behaviour. */}
      <div
        className={cn(
          "flex-shrink-0 bg-background",
          isMobile && "sticky z-40"
        )}
        style={isMobile ? { top: "64px" } : undefined}
      >
      {/* Top navigation bar */}
      <div className="border-b bg-background px-4 py-2 flex items-center gap-3 flex-wrap gap-y-2">
        {/* Prev/Next */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2"
            disabled={!hasPrev}
            onClick={() => hasPrev && navTo(recipes[currentIndex - 1].id)}
            title="Previous recipe (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[90px] text-center">
            {currentIndex >= 0
              ? `Recipe ${currentIndex + 1} of ${recipes.length}`
              : `– of ${recipes.length}`}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2"
            disabled={!hasNext}
            onClick={() => hasNext && navTo(recipes[currentIndex + 1].id)}
            title="Next recipe (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-7 text-xs w-40">
            <SelectValue placeholder="Filter…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="unreviewed">Unreviewed</SelectItem>
            <SelectItem value="validated">Validated</SelectItem>
            <SelectItem value="needs_correction">Needs Correction</SelectItem>
          </SelectContent>
        </Select>

        {/* Jump-to: by list position (N of total) or title search */}
        <div className="flex items-center gap-1">
          <Label className="text-xs text-muted-foreground sr-only">Jump to position or title</Label>
          <Input
            type="text"
            placeholder="Jump to # or title"
            className="h-7 text-xs w-36"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const val = (e.target as HTMLInputElement).value.trim();
                if (!val) return;
                // Positional jump: type "14" to go to the 14th recipe in the current filtered list
                const pos = parseInt(val, 10);
                if (!isNaN(pos) && pos >= 1 && pos <= recipes.length) {
                  navTo(recipes[pos - 1].id);
                  (e.target as HTMLInputElement).value = "";
                  return;
                }
                // Title search (case-insensitive, first match)
                const lc = val.toLowerCase();
                const match = recipes.find((r) => r.title.toLowerCase().includes(lc));
                if (match) {
                  navTo(match.id);
                  (e.target as HTMLInputElement).value = "";
                }
              }
            }}
          />
        </div>

        {/* Status badge */}
        {recipe && (
          <Badge
            variant="outline"
            className={cn("text-xs", statusColor(recipe.validationStatus))}
          >
            {statusLabel(recipe.validationStatus)}
          </Badge>
        )}

        {/* Save state */}
        <div className="ml-auto flex items-center gap-1 text-xs">
          {saveState === "saving" && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          )}
          {saveState === "saved" && (
            <span className="flex items-center gap-1 text-green-600">
              <Save className="h-3 w-3" /> Saved
            </span>
          )}
          {saveState === "error" && (
            <span className="flex items-center gap-1 text-destructive">
              <AlertCircle className="h-3 w-3" /> Save failed
            </span>
          )}
          {isDirty && saveState === "idle" && (
            <span className="text-muted-foreground">Unsaved…</span>
          )}
        </div>

        {/* Shortcuts hint */}
        <span className="text-xs text-muted-foreground hidden lg:block">
          Ctrl+← Ctrl+→ navigate · V validate · F flag · S save
        </span>
      </div>

      {/* Mobile tab toggle — rendered inside the sticky wrapper above so it stays
          pinned along with the toolbar while the recipe form scrolls. */}
      {isMobile && (
        <div className="bg-background/95 backdrop-blur border-b">
          <div className="flex" role="tablist" aria-label="Validate panel">
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "recipe"}
              onClick={() => setMobileTab("recipe")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors",
                mobileTab === "recipe"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Recipe details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "image"}
              onClick={() => setMobileTab("image")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors",
                mobileTab === "image"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              Image
            </button>
          </div>
        </div>
      )}
      </div>{/* end sticky toolbar wrapper */}

      {/* Main panel area: side-by-side with draggable divider on desktop, single full-width panel on mobile.
          Use `dvh` (dynamic viewport height) on mobile so the layout adapts to browser chrome appearing/disappearing. */}
      <div
        ref={containerRef}
        className={cn(
          "flex-1 min-h-0 overflow-hidden",
          isMobile ? "flex flex-col" : "flex"
        )}
        style={
          isMobile
            ? { height: "calc(100dvh - 312px)", minHeight: "360px" }
            : { height: "calc(100vh - 120px)" }
        }
      >
        {/* Image viewer — left on desktop, hidden when not the active mobile tab.
            On mobile we only mount the viewer when the image tab is active. Mounting it inside a
            `display: none` container would give it a 0×0 sizing context and break the initial fit/centering
            logic in ImageViewer (which reads container.clientWidth/clientHeight on image load). */}
        <div
          className={cn(
            "overflow-hidden",
            isMobile
              ? cn("flex-1 min-h-0 w-full", mobileTab !== "image" && "hidden")
              : "flex-shrink-0 border-r"
          )}
          style={!isMobile ? { width: `${leftPct}%` } : undefined}
        >
          {recipeLoading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !isMobile || mobileTab === "image" ? (
            <ImageViewer
                images={sourceImages}
                recipeId={currentId}
                onApplyExtraction={handleApplyExtraction}
              />
          ) : null}
        </div>

        {/* Draggable divider — desktop only */}
        {!isMobile && (
          <div
            className="w-1 cursor-col-resize bg-border hover:bg-primary/40 transition-colors flex-shrink-0 select-none"
            onMouseDown={() => { dragging.current = true; }}
          />
        )}

        {/* Recipe form — right on desktop, hidden when not the active mobile tab.
            Always mounted so unsaved edits and scroll position persist across tab switches. */}
        <div
          className={cn(
            "min-w-0 flex flex-col",
            isMobile
              ? cn("flex-1 min-h-0 w-full", mobileTab !== "recipe" && "hidden")
              : "flex-1 overflow-hidden"
          )}
        >
          {recipeLoading || !form ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="flex-1">
              <div className="p-5 space-y-5 pb-8">
                {/* Duplicate warning banner */}
                {recipe?.potentialDuplicateOfId && (
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">Possible duplicate</span>
                      {" — "}this recipe may be a duplicate of{" "}
                      <button
                        type="button"
                        className="underline font-medium hover:text-amber-700"
                        onClick={() => navTo(recipe.potentialDuplicateOfId!)}
                      >
                        {recipe.potentialDuplicateTitle ?? `Recipe #${recipe.potentialDuplicateOfId}`}
                      </button>
                      .
                    </div>
                    <button
                      type="button"
                      className="shrink-0 flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-800 border border-amber-300 rounded px-2 py-1 hover:bg-amber-100 transition-colors"
                      onClick={() => {
                        if (!currentId) return;
                        updateMutation.mutate({
                          id: currentId,
                          data: { potentialDuplicateOfId: null, potentialDuplicateTitle: null },
                        });
                      }}
                    >
                      <X className="h-3 w-3" />
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Title */}
                <div>
                  <Label className="text-xs text-muted-foreground">Title</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    className="mt-1 text-base font-semibold font-serif"
                    placeholder="Recipe title…"
                  />
                </div>

                {/* Row: Author / Category / Cuisine */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Author / Source</Label>
                    <Input
                      value={form.authorSource}
                      onChange={(e) => setField("authorSource", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="e.g. Aunt Carol"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <Select
                      value={form.category || "none"}
                      onValueChange={(v) => setField("category", v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1 h-9 text-sm">
                        <SelectValue placeholder="Category…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Cuisine</Label>
                    <Input
                      list="cuisine-suggestions"
                      value={form.cuisine}
                      onChange={(e) => setField("cuisine", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="e.g. American"
                    />
                    <datalist id="cuisine-suggestions">
                      <option value="American" />
                      <option value="Italian" />
                      <option value="Mexican" />
                      <option value="Chinese" />
                      <option value="Japanese" />
                      <option value="French" />
                      <option value="Indian" />
                      <option value="Thai" />
                      <option value="Mediterranean" />
                      <option value="Greek" />
                      <option value="Spanish" />
                      <option value="Middle Eastern" />
                      <option value="Korean" />
                      <option value="Vietnamese" />
                      <option value="German" />
                      <option value="British" />
                      <option value="Southern" />
                      <option value="Tex-Mex" />
                      <option value="Caribbean" />
                      <option value="African" />
                    </datalist>
                  </div>
                </div>

                {/* Row: Times + Yield + Difficulty + Source type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Prep Time</Label>
                    <Input
                      value={form.prepTime}
                      onChange={(e) => setField("prepTime", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="15 min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Cook Time</Label>
                    <Input
                      value={form.cookTime}
                      onChange={(e) => setField("cookTime", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="30 min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Time</Label>
                    <Input
                      value={form.totalTime}
                      onChange={(e) => setField("totalTime", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="45 min"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Yield / Servings</Label>
                    <Input
                      value={form.yieldServings}
                      onChange={(e) => setField("yieldServings", e.target.value)}
                      className="mt-1 text-sm"
                      placeholder="Serves 4"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Difficulty</Label>
                    <Select
                      value={form.difficulty || "none"}
                      onValueChange={(v) => setField("difficulty", v === "none" ? "" : v)}
                    >
                      <SelectTrigger className="mt-1 h-9 text-sm">
                        <SelectValue placeholder="Difficulty…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {DIFFICULTIES.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Source Type</Label>
                    <div className="mt-2 flex flex-col gap-1">
                      {SOURCE_TYPES.map((s) => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            name="sourceType"
                            value={s}
                            checked={form.sourceType === s}
                            onChange={() => setField("sourceType", s)}
                            className="accent-primary"
                          />
                          <span className="capitalize">{s}</span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
                        <input
                          type="radio"
                          name="sourceType"
                          value=""
                          checked={!form.sourceType}
                          onChange={() => setField("sourceType", "")}
                          className="accent-primary"
                        />
                        <span>—</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Ingredients */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-xs text-muted-foreground">
                      Ingredients ({form.ingredients.length})
                    </Label>
                  </div>
                  <ScaleControl scale={scaleFactor} onChange={setScaleFactor} />
                  <div className="mt-2">
                    <IngredientsEditor
                      ingredients={form.ingredients}
                      onChange={(ings) => setField("ingredients", ings)}
                      scaleFactor={scaleFactor}
                    />
                  </div>
                </div>

                {/* Directions */}
                <div>
                  <Label className="text-xs text-muted-foreground">Directions</Label>
                  <Textarea
                    value={form.directions}
                    onChange={(e) => setField("directions", e.target.value)}
                    className="mt-1 text-sm min-h-[140px] font-mono leading-relaxed"
                    placeholder="Step-by-step instructions…"
                  />
                </div>

                {/* Serving suggestions */}
                <div>
                  <Label className="text-xs text-muted-foreground">Serving Suggestions</Label>
                  <Textarea
                    value={form.servingSuggestions}
                    onChange={(e) => setField("servingSuggestions", e.target.value)}
                    className="mt-1 text-sm min-h-[60px]"
                    placeholder="Optional…"
                  />
                </div>

                {/* Handwritten notes — warm yellow tint */}
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Handwritten Notes <span className="text-xs italic">(from original)</span>
                  </Label>
                  <Textarea
                    value={form.handwrittenNotes}
                    onChange={(e) => setField("handwrittenNotes", e.target.value)}
                    className="mt-1 text-sm min-h-[60px] italic bg-yellow-50 border-yellow-200 text-yellow-900 placeholder:text-yellow-400"
                    placeholder="Notes visible on the original image…"
                  />
                </div>

                {/* Transcriber notes */}
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Transcriber Notes <span className="text-xs italic">(your own)</span>
                  </Label>
                  <Textarea
                    value={form.transcriberNotes}
                    onChange={(e) => setField("transcriberNotes", e.target.value)}
                    className="mt-1 text-sm min-h-[60px]"
                    placeholder="Your personal notes…"
                  />
                </div>

                {/* ── Action buttons ────────────────────────────────────── */}
                <div className="border-t pt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                    disabled={!currentId || validateMutation.isPending || updateMutation.isPending}
                    onClick={handleValidate}
                    title="V — saves edits then marks validated and advances"
                  >
                    {(validateMutation.isPending || updateMutation.isPending)
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Mark Validated
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50"
                    disabled={!currentId || flagMutation.isPending}
                    onClick={() => currentId && flagMutation.mutate({ id: currentId })}
                    title="F"
                  >
                    {flagMutation.isPending
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Flag className="h-3.5 w-3.5" />}
                    Needs Correction
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={!currentId}
                    onClick={() => setReextractDialog(true)}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Re-extract
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={!currentId}
                    onClick={() => {
                      setSplitNewTitle("");
                      setSplitDialog(true);
                    }}
                  >
                    <Scissors className="h-3.5 w-3.5" />
                    Split
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={!hasPrev}
                    onClick={() => {
                      if (!currentId || !hasPrev) return;
                      mergeMutation.mutate({ id: currentId, otherId: recipes[currentIndex - 1].id });
                    }}
                    title="Merge previous recipe into this one"
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    ← Merge Prev
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={!hasNext}
                    onClick={() => {
                      if (!currentId || !hasNext) return;
                      mergeMutation.mutate({ id: currentId, otherId: recipes[currentIndex + 1].id });
                    }}
                    title="Merge next recipe into this one"
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Merge Next →
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={mergeOptions.length === 0}
                    onClick={() => { setMergeTargetId(null); setMergeDialog(true); }}
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Merge…
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-destructive hover:bg-destructive/10 border-destructive/40 ml-auto"
                    disabled={!currentId}
                    onClick={() => setDeleteDialog(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </div>

                {/* Recipe ID footnote */}
                {recipe && (
                  <p className="text-xs text-muted-foreground/60">
                    Recipe ID #{recipe.id} · Created {recipe.createdAt
                      ? new Date(recipe.createdAt).toLocaleDateString()
                      : "unknown"}
                    {recipe.updatedAt && ` · Updated ${new Date(recipe.updatedAt).toLocaleDateString()}`}
                  </p>
                )}

                {/* Nutrition estimate panel */}
                <NutritionPanel
                  recipeId={currentId}
                  ingredients={form.ingredients}
                  scaleFactor={scaleFactor}
                  onNutritionLoaded={setNutritionData}
                />
              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* ── Delete dialog ─────────────────────────────────────────────────── */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{recipe?.title}</strong> will be permanently deleted. The source
              images will remain and can be re-extracted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={() => {
                if (currentId) deleteMutation.mutate({ id: currentId });
                setDeleteDialog(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Re-extract dialog ─────────────────────────────────────────────── */}
      <AlertDialog open={reextractDialog} onOpenChange={setReextractDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-extract this recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-run Claude AI on the original source image(s) and <strong>overwrite</strong>{" "}
              all fields of <strong>{recipe?.title}</strong> with the new extraction. Any manual edits
              will be lost. The recipe ID is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (currentId) reextractMutation.mutate({ id: currentId });
                setReextractDialog(false);
              }}
            >
              {reextractMutation.isPending ? "Re-extracting…" : "Re-extract"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Merge dialog ──────────────────────────────────────────────────── */}
      <Dialog open={mergeDialog} onOpenChange={setMergeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Merge Recipe</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Merge another recipe into <strong>{recipe?.title}</strong>. The selected recipe
            will be deleted and its ingredients + source images moved here.
          </p>
          <div className="mt-3">
            <Label className="text-xs text-muted-foreground">Select recipe to merge in:</Label>
            <ScrollArea className="h-48 mt-2 border rounded-md">
              {mergeOptions.map((r) => (
                <button
                  key={r.id}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-b last:border-0",
                    mergeTargetId === r.id && "bg-primary/10 font-medium"
                  )}
                  onClick={() => setMergeTargetId(r.id)}
                >
                  <div className="truncate">{r.title}</div>
                  <div className="text-xs text-muted-foreground flex gap-2 mt-0.5">
                    <span>#{r.id}</span>
                    {r.category && <span>{r.category}</span>}
                    <Badge variant="outline" className={cn("text-[10px] h-4", statusColor(r.validationStatus))}>
                      {statusLabel(r.validationStatus)}
                    </Badge>
                  </div>
                </button>
              ))}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog(false)}>
              Cancel
            </Button>
            <Button
              disabled={!mergeTargetId || mergeMutation.isPending}
              onClick={() => {
                if (!currentId || !mergeTargetId) return;
                mergeMutation.mutate({ id: currentId, otherId: mergeTargetId });
              }}
            >
              {mergeMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Merging…</>
              ) : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Split dialog ───────────────────────────────────────────────────── */}
      <Dialog open={splitDialog} onOpenChange={setSplitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate Recipe</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Creates an exact duplicate of this recipe (including all ingredients and metadata).
            You can then edit each copy to differentiate them.
          </p>

          <div className="mt-3">
            <Label className="text-xs text-muted-foreground">New recipe title (optional)</Label>
            <Input
              value={splitNewTitle}
              onChange={(e) => setSplitNewTitle(e.target.value)}
              placeholder={`${form?.title ?? "Recipe"} (2)`}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank to use the default title.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitDialog(false)}>
              Cancel
            </Button>
            <Button
              disabled={splitMutation.isPending}
              onClick={() => {
                if (!currentId) return;
                splitMutation.mutate({
                  id: currentId,
                  data: { newTitle: splitNewTitle.trim() || undefined },
                });
              }}
            >
              {splitMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Duplicating…</>
              ) : "Duplicate Recipe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile primary-action bar.
          The in-form Validate / Flag buttons live near the bottom of the recipe
          form and would otherwise require scrolling on phone-sized viewports
          (where the V / F keyboard shortcuts aren't usable). This fixed bar sits
          just above the app's bottom nav (`bottom-16` matches the nav's h-16)
          so the primary review actions are always one tap away. The desktop
          in-form buttons remain unchanged. */}
      {isMobile && (
        <div
          className="md:hidden fixed inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border/40"
          style={{ bottom: "64px" }}
        >
          <div className="flex items-stretch gap-2 px-3 py-2">
            <Button
              size="sm"
              className="flex-1 h-10 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
              disabled={!currentId || validateMutation.isPending || updateMutation.isPending}
              onClick={handleValidate}
            >
              {(validateMutation.isPending || updateMutation.isPending)
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <CheckCircle2 className="h-4 w-4" />}
              Validate
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-10 gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50"
              disabled={!currentId || flagMutation.isPending}
              onClick={() => currentId && flagMutation.mutate({ id: currentId })}
            >
              {flagMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Flag className="h-4 w-4" />}
              Flag
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
